import api from '@/services/api';
import type {
    AddressFindResponse,
    AddressLookupResult,
    AddressRetrieveResponse,
    NormalisedAddress,
} from '../types/addressLookup.types';

const KNOWN_TYPES = new Set(['address', 'container']);

// Drop rows with an unusable type once here, rather than branching on it in the UI.
const toResult = (row: Partial<AddressLookupResult>): AddressLookupResult | null => {
    if (!row.id || !row.type || !KNOWN_TYPES.has(row.type)) return null;
    return {
        id: row.id,
        type: row.type,
        text: row.text ?? '',
        description: row.description ?? '',
    };
};

export const findAddresses = async (
    params: { text: string; container?: string },
    signal?: AbortSignal,
): Promise<AddressLookupResult[]> => {
    const response = await api.get<AddressFindResponse>('/addresses/find', {
        params: {
            text: params.text,
            ...(params.container ? { container: params.container } : {}),
        },
        signal,
        // Optional convenience control with its own fallback — never the global 403 toast.
        suppressGlobalForbiddenToast: true,
    });

    return (response.data.results ?? [])
        .map(toResult)
        .filter((r): r is AddressLookupResult => r !== null);
};

export const retrieveAddress = async (id: string): Promise<NormalisedAddress> => {
    const response = await api.get<AddressRetrieveResponse>('/addresses/retrieve', {
        params: { id },
        suppressGlobalForbiddenToast: true,
    });

    const a = response.data.address ?? {};
    // Coerced to '', not left undefined — setValue(name, undefined) uncontrols a Controller field.
    return {
        addressLine1: a.addressLine1 ?? '',
        addressLine2: a.addressLine2 ?? '',
        city: a.city ?? '',
        postcode: a.postcode ?? '',
    };
};
